<script setup>
import { ref, computed, watch, onBeforeUnmount, getCurrentInstance } from 'vue';
import SidebarToggle from '../../common/sidebar-toggle/SidebarToggle';
import SiteTreeNavigationMenu from '../../common/site-tree/SiteTreeNavigationMenu';
import SiteTree from '../../common/site-tree/SiteTree';
import FormNavAndForm from '../../common/form/FormNavAndForm';
import FormHeader from '../../common/form/FormHeader';

const { proxy } = getCurrentInstance();
const store = proxy.$store;
const router = proxy.$router;

// --- State ---
const formId = ref(undefined);
const tableName = ref(undefined);
const itemId = ref(undefined);
const siteTreeId = 'siteTree';

// --- Computed ---
const showSaveAndCloseBtn = computed(() => tableName.value === 'content');

// --- Methods ---

const goTo = (id) => {
    router.push({ name: 'siteTree', params: { pageOrContentId: id } });
};

const goToHome = () => {
    goTo(store.state.settings.homePage);
};

const onSave = (obj) => {
    if (tableName.value === 'pages' && store.state.siteTree.selectedPageId !== obj.id) {
        goTo(obj.id);
    }
    store.dispatch('siteTree/init');
};

const createForm = () => {
    if (formId.value) {
        store.unregisterModule(formId.value);
    }

    formId.value = `form:${tableName.value}:${itemId.value}`;

    store.dispatch('forms/createForm', {
        tableName: tableName.value,
        id: itemId.value,
        onSave: onSave,
        onInit: () => {
            if (itemId.value === 0 && tableName.value === 'pages') {
                const lastId = store.state.siteTree.lastSelectedPageId;
                if (lastId) {
                    store.dispatch('itemData/get', { tableName: 'pages', id: lastId })
                        .then((obj) => {
                            store.commit(`${formId.value}/presetData`, { name: 'navigation', value: obj.navigation });
                            store.commit(`${formId.value}/presetData`, { name: 'parent', value: obj.parent });
                        });
                }
            }
        }
    });
};

const init = () => {
    // Pull ID directly from proxy to ensure we have the latest route state
    let id = proxy.$route.params.pageOrContentId;

    if (id) {
        if (typeof id === 'string' && id.indexOf('c') === 0) {
            id = parseInt(id.substring(1));
            store.dispatch(`${siteTreeId}/setContentId`, id);
            tableName.value = 'content';

            if (id > 0) {
                const obj = store.state.pageContentData.items.find(o => o.id === id);
                if (!obj) {
                    goToHome();
                    return;
                }
                itemId.value = obj.contentId;
                store.commit(`${siteTreeId}/addExpandedPageId`, obj.pageId);
                store.dispatch(`${siteTreeId}/setExpandedPageIds`, obj.pageId);
                createForm();
            } else {
                itemId.value = 0;
                createForm();
            }
        } else {
            tableName.value = 'pages';
            id = parseInt(id);
            store.dispatch(`${siteTreeId}/setPageId`, id);
            store.dispatch(`${siteTreeId}/setExpandedPageIds`, id);
            itemId.value = id;
            createForm();
        }
    } else {
        const state = store.state.siteTree;
        if (state.selectedPageId) {
            goTo(state.selectedPageId);
        } else if (state.selectedContentId) {
            goTo(`c${state.selectedContentId}`);
        } else {
            goToHome();
        }
    }
};

// --- Watchers ---

// Deep watch the route to catch param changes in Vue 2.7
watch(
    () => proxy.$route.params.pageOrContentId,
    (newVal, oldVal) => {
        if (newVal !== oldVal) {
            init();
        }
    }
);

// --- Lifecycle ---

store.commit('navigationComponent', 'site_tree');

const parentChildIds = store.state.siteTree.parentChildIds;
if (Object.keys(parentChildIds).length === 0) {
    store.dispatch(`${siteTreeId}/init`, {
        onPageSelect: (pId) => {
            store.dispatch(`${siteTreeId}/setPageId`, pId);
            goTo(pId);
        },
        onContentSelect: (cId) => {
            store.dispatch(`${siteTreeId}/setContentId`, cId);
            goTo(`c${cId}`);
        }
    });
}

// Initial run for the first load
init();

onBeforeUnmount(() => {
    if (formId.value) {
        store.unregisterModule(formId.value);
    }
});
</script>

<template>
    <SidebarToggle :name="'site_tree'" class="d-flex h-100 overflow-auto">
        <template v-slot:leftSidebar>
            <div class="flex-grow-1">
                <div class="text-center">
                    <SiteTreeNavigationMenu :site-tree-id="siteTreeId" class="m-3 d-inline-block"/>
                </div>

                <SiteTree :site-tree-id="siteTreeId"/>
            </div>
        </template>

        <section class="d-flex h-100 overflow-auto flex-grow-1 flex-column">
            <FormHeader
                v-if="formId"
                :key="'header-' + formId"
                :form-id="formId"
                :show-add-btn="true"
                :show-save-and-close-btn="showSaveAndCloseBtn"
                :show-save-btn="true"
            />

            <FormNavAndForm
                v-if="formId"
                :key="'body-' + formId"
                :form-id="formId"
                :show-archive-btn="true"
                :show-duplicate-btn="true"
                :show-history-btn="true"
            />
        </section>
    </SidebarToggle>
</template>